import { useCallback, useEffect, useRef } from 'react';

export function useCoalescedRefresh(callback, delay = 350) {
  const callbackRef = useRef(callback);
  const stateRef = useRef({
    timer: null,
    running: false,
    queued: false,
    args: [],
    disposed: false,
  });

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const execute = useCallback(async () => {
    const state = stateRef.current;
    if (state.disposed) return;
    if (state.running) {
      state.queued = true;
      return;
    }

    state.running = true;
    state.queued = false;
    const args = state.args;
    try {
      await callbackRef.current(...args);
    } catch (_error) {
      // The loaders keep their existing error handling. This prevents an
      // unhandled rejection from breaking future scheduled refreshes.
    } finally {
      state.running = false;
      if (state.disposed || !state.queued) return;
      state.queued = false;
      if (state.timer) window.clearTimeout(state.timer);
      state.timer = window.setTimeout(() => {
        state.timer = null;
        execute();
      }, delay);
    }
  }, [delay]);

  const schedule = useCallback((...args) => {
    const state = stateRef.current;
    if (state.disposed) return;
    state.args = args;
    state.queued = true;
    if (state.timer) window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => {
      state.timer = null;
      execute();
    }, delay);
  }, [delay, execute]);

  useEffect(() => {
    const state = stateRef.current;
    state.disposed = false;
    return () => {
      state.disposed = true;
      if (state.timer) window.clearTimeout(state.timer);
    };
  }, []);

  return schedule;
}

export default useCoalescedRefresh;
